import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserProfile from '../components/UserProfile';
import NotificationBadge from '../components/NotificationBadge';
import NotificationCenter from '../components/NotificationCenter';
import PageFooter from '../components/PageFooter';
import {
  HomeIcon,
  UsersIcon,
  Bars3Icon,
  XMarkIcon,
  DocumentDuplicateIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * AdminLayout - 管理員/營運人員專用佈局
 * 包含完整的系統功能導航
 */
const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const location = useLocation();
  const { isAdmin } = useAuth();

  // 底部主列
  const mainNavigation = [
    { name: '儀表板', href: '/admin/dashboard', icon: HomeIcon },
    { name: '客戶', href: '/admin/customers', icon: UsersIcon },
    { name: '報價單', href: '/admin/quotes', icon: DocumentDuplicateIcon },
    { name: '計算機', href: '/admin/overseas-calculator', icon: CalculatorIcon },
  ];

  // 漢堡／更多選單
  const moreNavigation = [
    { name: '設定', href: '/admin/settings', icon: Cog6ToothIcon },
    ...(isAdmin() ? [{ name: '使用者管理', href: '/admin/users', icon: ShieldCheckIcon }] : []),
  ];

  const isMoreActive = moreNavigation.some(
    (item) => location.pathname === item.href || location.pathname.startsWith(item.href + '/')
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-ci-bg">
      {/* 行動側邊欄（更多選單） */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 flex z-40">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <img 
                  src="/Logo-with-name.svg" 
                  alt="Teleworker CRM" 
                  className="h-10 w-auto"
                />
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {moreNavigation.map((item) => {
                  const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-base font-medium rounded-lg ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-ci-text hover:bg-gray-50 hover:text-ci-text'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon
                        className={`mr-4 flex-shrink-0 h-6 w-6 ${
                          isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <UserProfile />
            </div>
          </div>
        </div>
      </div>

      {/* 主要內容區域 */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* 頂部導航列 */}
        <div className="flex items-center justify-between bg-white border-b border-gray-200 px-3 py-2.5 relative z-10">
          <div className="flex items-center">
            <img 
              src="/Logo.svg" 
              alt="Teleworker" 
              className="h-7 w-auto"
            />
          </div>
          <div className="flex items-center space-x-2">
            <NotificationBadge onClick={() => setNotificationCenterOpen(true)} />
            <div className="flex items-center">
              <UserProfile collapsed={false} position="top" />
            </div>
          </div>
        </div>

        {/* 主要內容 */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none pb-16">
          <div className="p-3 lg:p-6 min-h-full flex flex-col">
            <div className="flex-1">
              {children}
            </div>
            <PageFooter />
          </div>
        </main>

        {/* 底部導航欄 */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
          <nav className="flex items-center justify-around px-2 py-1.5">
            {mainNavigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-lg transition-colors ${
                    isActive
                          ? 'text-primary'
                      : 'text-gray-500 hover:text-ci-text'
                  }`}
                >
                  <item.icon
                    className={`h-6 w-6 mb-1 ${isActive ? 'text-primary' : 'text-gray-400'}`}
                    aria-hidden="true"
                  />
                  <span className="text-xs font-medium">{item.name}</span>
                </Link>
              );
            })}
            {moreNavigation.length > 0 && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-lg transition-colors ${
                  sidebarOpen || isMoreActive
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-ci-text'
                }`}
              >
                <Bars3Icon
                  className={`h-6 w-6 mb-1 ${sidebarOpen || isMoreActive ? 'text-primary' : 'text-gray-400'}`}
                />
                <span className="text-xs font-medium">更多</span>
              </button>
            )}
          </nav>
        </div>

        {/* 通知中心 */}
        <NotificationCenter
          isOpen={notificationCenterOpen}
          onClose={() => setNotificationCenterOpen(false)}
        />
      </div>
    </div>
  );
};

export default AdminLayout;

